import { useCallback, useMemo, useState } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { colorsPalettes } from "@similarweb/styles";
import { Stack, Text } from "@similarweb/ui-components/foundations";

import { SelectCombobox } from "app/pages/sales-intelligence/common-components/dropdown/SelectCombobox";
import { renderCheckboxOption } from "app/pages/sales-intelligence/common-components/dropdown/utils/renderCheckboxOption";
import { FilterSuggestionsBar } from "app/pages/sales-intelligence/common-components/FilterSuggestionsBar";
import InclusionGroup from "app/pages/sales-intelligence/modules/advanced-search-filtering/components/atoms/InclusionGroup";
import type { IFilterViewBaseProps } from "app/pages/sales-intelligence/modules/advanced-search-filtering/types/filterInstance";
import { type InclusionOption, inclusionOptionMap } from "app/pages/sales-intelligence/types/commonMaps";

import type IabCategoriesFilter from "../../../models/IabCategoriesFilter/IabCategoriesFilter";
import type { IIabCategoryItemDescriptionDto } from "../../../types/descriptionDto";

import useIabCategoriesFilter from "./hooks/useIabCategoriesSuggestions";

interface IIabCategoryDropdownItem extends IIabCategoryItemDescriptionDto {
    text: string;
}

const IabCategoriesFilterView = ({ filter, value, onFilterUpdate }: IFilterViewBaseProps<IabCategoriesFilter>) => {
    const { translate } = useTranslate();

    const [localInclusionValue, setLocalInclusionValue] = useState<InclusionOption>(inclusionOptionMap.includeOnly);

    const categories = useMemo((): IIabCategoryDropdownItem[] => {
        if (!filter.options) {
            return [];
        }

        return filter.options.map((option) => ({
            ...option,
            text: option.name,
        }));
    }, [filter.options]);

    const handleInclusionChange = (inclusion: InclusionOption) => {
        setLocalInclusionValue(inclusion);

        if (!value?.values?.length) {
            return;
        }

        onFilterUpdate({
            ...value,
            inclusion,
        });
    };

    const handleSelect = useCallback(
        (category?: IIabCategoryItemDescriptionDto) => {
            if (!category) {
                return;
            }

            const values = value?.values || [];

            onFilterUpdate({
                values: values.includes(category.id)
                    ? values.filter((id) => id !== category.id)
                    : values.concat(category.id),
                inclusion: localInclusionValue,
            });
        },
        [value?.values, localInclusionValue],
    );

    const { isFilterDirty, isLoading, suggestedCategoryChips } = useIabCategoriesFilter({
        value,
        filter,
        handleSelect,
    });

    return (
        <Stack gap="16px">
            <div>
                <InclusionGroup
                    value={localInclusionValue}
                    onChange={handleInclusionChange}
                />
            </div>

            <div>
                <FilterSuggestionsBar
                    suggestedItems={suggestedCategoryChips}
                    isEnabled={isFilterDirty}
                    isLoading={isLoading}
                />

                <Stack
                    width="260px"
                    gap="8px"
                >
                    <Text
                        fontVariant="sectionTitle"
                        color={colorsPalettes.carbon[400]}
                    >
                        {translate("common.category")}
                    </Text>

                    <SelectCombobox<IIabCategoryDropdownItem>
                        items={categories}
                        isItemSelected={(item) => (value?.values || []).includes(item.id)}
                        onSelect={handleSelect}
                        buttonText={translate("si.find_apps.filters.iab-categories.dd-button-text")}
                        width={260}
                        hasSearch
                        searchPlaceHolder={translate("si.find_apps.filters.iab-categories.dd-placeholder")}
                        renderOption={renderCheckboxOption}
                    />
                </Stack>
            </div>
        </Stack>
    );
};

export default IabCategoriesFilterView;
